import { Smile } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';
import { Textarea } from '@/shared/ui/textarea';
import { Label } from '@/shared/ui/label';

/** Conjunto enxuto (legado usava emojioneArea — aqui um picker leve sem dependência). */
const EMOJI_GROUPS: { label: string; items: string[] }[] = [
  {
    label: 'Frequentes',
    items: ['😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇', '🙂', '😉', '😍', '🥰', '😘', '😜', '🤩', '😎'],
  },
  {
    label: 'Gestos',
    items: ['👍', '👎', '👏', '🙌', '🤝', '🙏', '💪', '✌️', '🤞', '👋', '🤗', '🫡'],
  },
  {
    label: 'Objetos',
    items: ['🎁', '🎉', '🎊', '🛒', '💳', '⭐', '🔥', '💡', '📢', '🔔', '❤️', '🧡', '💛', '💚', '💙', '💜'],
  },
];

interface MessageFieldProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  placeholder?: string;
  id?: string;
}

/** Picker leve, o mesmo conjunto do campo de mensagem das campanhas. */
export function EmojiInsertButton({
  onInsert,
  className,
}: {
  onInsert: (emoji: string) => void;
  className?: string;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="ghost" size="sm" className={cn('h-8 gap-1.5 text-muted-foreground', className)}>
          <Smile className="size-4" /> Emoji
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="end">
        <div className="max-h-64 space-y-3 overflow-y-auto">
          {EMOJI_GROUPS.map((group) => (
            <div key={group.label}>
              <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{group.label}</p>
              <div className="grid grid-cols-6 gap-1.5">
                {group.items.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    className="grid size-8 place-items-center rounded-md text-lg hover:bg-accent"
                    onClick={() => onInsert(emoji)}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Textarea de mensagem com inserção de emoji (paridade com emojioneArea do dash). */
export function MessageFieldWithEmoji({
  label = 'Mensagem',
  value,
  onChange,
  rows = 5,
  placeholder,
  id = 'campaign-message',
}: MessageFieldProps) {
  const insertEmoji = (emoji: string) => {
    const el = document.getElementById(id) as HTMLTextAreaElement | null;
    if (!el) {
      onChange(`${value}${emoji}`);
      return;
    }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const next = value.slice(0, start) + emoji + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + emoji.length;
      el.setSelectionRange(pos, pos);
    });
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={id}>{label}</Label>
        <EmojiInsertButton onInsert={insertEmoji} />
      </div>
      <Textarea id={id} rows={rows} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}
